import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StudentLayout } from './StudentLayout';
import { TeacherLayout } from './TeacherLayout';
interface RoleAwareLayoutProps {
  children: React.ReactNode;
}
/**
 * RoleAwareLayout - Renders the appropriate layout based on user role
 *
 * This component prevents layout mixing by ensuring:
 * - Students always see StudentLayout (with student sidebar)
 * - Teachers always see TeacherLayout (with teacher sidebar)
 * - Teachers must have verified email to access these pages
 *
 * Used for shared account pages that both roles can access.
 */
export const RoleAwareLayout: React.FC<RoleAwareLayoutProps> = ({
  children
}) => {
  const {
    currentUser,
    userType,
    loading
  } = useAuth();
  const navigate = useNavigate();
  // Enforce email verification for teachers accessing account pages
  useEffect(() => {
    if (!loading && currentUser && userType === 'teacher') {
      if (!currentUser.emailVerified) {
        console.log('[RoleAwareLayout] Teacher email not verified, redirecting to verification page');
        navigate('/teacher-email-verification', {
          replace: true
        });
      }
    }
  }, [currentUser, userType, loading, navigate]);
  // Show loading spinner while checking verification
  if (loading || userType === 'teacher' && currentUser && !currentUser.emailVerified) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-1261">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-deep-pink" data-id="element-1262"></div>
      </div>;
  }
  // Normalize userType to handle case variations
  const normalizedUserType = userType ? userType.toLowerCase().trim() : null;
  const isStudent = normalizedUserType === 'student';
  // Students get StudentLayout, everyone else gets TeacherLayout
  return isStudent ? <StudentLayout data-id="element-1263">{children}</StudentLayout> : <TeacherLayout data-id="element-1264">{children}</TeacherLayout>;
};